import { ref, computed, watch, onBeforeUnmount, type Ref, type ComputedRef } from 'vue';
import Hammer from 'hammerjs';
import { INITIAL_ZOOM, MIN_ZOOM, MAX_ZOOM } from '@/constant/files';

export type UseZoomOptions = {
  minZoom?: number;
  maxZoom?: number;
  doubleTapZoom?: number;
};

export type UseZoomReturn = {
  zoom: Ref<number>;
  tx: Ref<number>;
  ty: Ref<number>;
  isPinching: Ref<boolean>;
  contentStyle: ComputedRef<{ transform: string; transformOrigin: string; willChange: string }>;
};

export function useZoom(el: Ref<HTMLElement | undefined>, options?: UseZoomOptions): UseZoomReturn {
  const minZoom = options?.minZoom ?? MIN_ZOOM;
  const maxZoom = options?.maxZoom ?? MAX_ZOOM;
  const doubleTapZoom = options?.doubleTapZoom ?? 2.5;

  const zoom = ref(INITIAL_ZOOM);
  const tx = ref(0);
  const ty = ref(0);
  const isPinching = ref(false);

  let mc: HammerManager | null = null;
  let startZoom = INITIAL_ZOOM;
  let startTx = 0;
  let startTy = 0;

  const clampZoom = (v: number) => Math.min(maxZoom, Math.max(minZoom, v));

  const clampTranslate = (element: HTMLElement, newTx: number, newTy: number, scale: number) => {
    if (scale <= 1) return { x: 0, y: 0 };
    const w = element.clientWidth;
    // X: empêche de dépasser les bords gauche/droit
    const clampedX = Math.min(0, Math.max(w * (1 - scale), newTx));
    // Y: empêche de remonter au-dessus du contenu, pas de borne basse (PDF peut être très long)
    const clampedY = Math.min(0, newTy);
    return { x: clampedX, y: clampedY };
  };

  const contentStyle = computed(() => ({
    transform: `translate(${tx.value}px, ${ty.value}px) scale(${zoom.value})`,
    transformOrigin: '0 0',
    willChange: 'transform',
  }));

  function reset() {
    zoom.value = INITIAL_ZOOM;
    tx.value = 0;
    ty.value = 0;
  }

  function setupHammer(element: HTMLElement) {
    mc = new Hammer.Manager(element, { touchAction: 'none' });
    element.style.touchAction = 'none';

    const pinch = new Hammer.Pinch();
    const pan = new Hammer.Pan({ threshold: 0, direction: Hammer.DIRECTION_ALL });
    const doubleTap = new Hammer.Tap({ event: 'doubletap', taps: 2 });

    pinch.recognizeWith(pan);
    mc.add([pinch, pan, doubleTap]);

    // ─── Pinch ─────────────────────────────────────────────────────────
    mc.on('pinchstart', () => {
      isPinching.value = true;
      startZoom = zoom.value;
      startTx = tx.value;
      startTy = ty.value;
    });

    mc.on('pinchmove', (ev: HammerInput) => {
      const rect = element.getBoundingClientRect();
      const cx = ev.center.x - rect.left;
      const cy = ev.center.y - rect.top;

      const newZoom = clampZoom(startZoom * ev.scale);
      const ratio = newZoom / startZoom;

      // Le point sous les doigts reste fixe
      const newTx = cx - (cx - startTx) * ratio;
      const newTy = cy - (cy - startTy) * ratio;
      const clamped = clampTranslate(element, newTx, newTy, newZoom);

      zoom.value = newZoom;
      tx.value = clamped.x;
      ty.value = clamped.y;
    });

    mc.on('pinchend pinchcancel', () => {
      isPinching.value = false;
      if (zoom.value <= minZoom) reset();
    });

    // ─── Pan ───────────────────────────────────────────────────────────
    mc.on('panstart', () => {
      startTx = tx.value;
      startTy = ty.value;
    });

    mc.on('panmove', (ev: HammerInput) => {
      if (isPinching.value) return;

      if (zoom.value <= 1) {
        // Scroll vertical uniquement : borné entre le haut (0) et le bas du contenu
        const containerH = element.clientHeight;
        const contentH = (element.firstElementChild as HTMLElement)?.offsetHeight ?? 0;
        const minTy = -Math.max(0, contentH - containerH);
        ty.value = Math.min(0, Math.max(minTy, startTy + ev.deltaY));
        return;
      }

      const newTx = startTx + ev.deltaX;
      const newTy = startTy + ev.deltaY;
      const clamped = clampTranslate(element, newTx, newTy, zoom.value);
      tx.value = clamped.x;
      ty.value = clamped.y;
    });

    // ─── Double tap : toggle zoom ───────────────────────────────────────
    mc.on('doubletap', (ev: HammerInput) => {
      if (zoom.value > INITIAL_ZOOM) {
        reset();
      } else {
        const rect = element.getBoundingClientRect();
        const cx = ev.center.x - rect.left;
        const cy = ev.center.y - rect.top;
        const newZoom = doubleTapZoom;
        const clamped = clampTranslate(element, cx * (1 - newZoom), cy * (1 - newZoom), newZoom);
        zoom.value = newZoom;
        tx.value = clamped.x;
        ty.value = clamped.y;
      }
    });
  }

  function destroyHammer() {
    mc?.destroy();
    mc = null;
  }

  watch(zoom, (newZoom) => {
    if (newZoom <= minZoom) {
      tx.value = 0;
      ty.value = 0;
    }
  });

  watch(
    () => el.value,
    (newEl) => {
      destroyHammer();
      if (newEl) setupHammer(newEl);
    },
    { immediate: true },
  );

  onBeforeUnmount(destroyHammer);

  return { zoom, tx, ty, isPinching, contentStyle };
}
